import React from "react";
import { format, formatDistanceToNow } from "date-fns";
import { Image, CheckCircle, AlertCircle, Clock, MapPin, Brain, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";

export default function RecentActivity({ photos, syncLogs }) {
  const activities = [
    ...photos.slice(0, 8).map(p => {
      const timeStr = p.taken_at || p.processed_at || p.created_at;
      const propertyInfo = p.property_name || (p.source_dropbox_path ? p.source_dropbox_path.split('/').pop() : null);
      
      return {
        type: "photo",
        id: p.id,
        photo: p,
        title: propertyInfo || "Photo uploaded",
        subtitle: p.inspector_name || "Unknown inspector",
        location: p.gps_lat && p.gps_lng ? `${p.gps_lat.toFixed(4)}, ${p.gps_lng.toFixed(4)}` : null,
        status: p.assignment_status,
        analysisStatus: p.analysis_status,
        time: timeStr,
        timestamp: new Date(timeStr)
      };
    }),
    ...syncLogs.slice(0, 3).map(s => ({
      type: "sync",
      id: s.id,
      title: `Sync completed`,
      subtitle: `${s.files_processed || 0} files processed`,
      status: s.status,
      time: s.created_at,
      timestamp: new Date(s.created_at)
    }))
  ].sort((a, b) => b.timestamp - a.timestamp).slice(0, 10);

  const getStatusIcon = (status) => {
    switch (status) {
      case "auto_matched":
      case "manual_assigned":
      case "success":
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case "unassigned":
      case "out_of_range":
        return <AlertCircle className="w-4 h-4 text-amber-500" />;
      case "failed":
        return <AlertCircle className="w-4 h-4 text-rose-500" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getAnalysisBadge = (status) => {
    if (!status || status === "pending") return null;
    
    const badges = {
      completed: { label: "Analyzed", className: "bg-violet-100 text-violet-700" },
      analyzing: { label: "Analyzing", className: "bg-blue-100 text-blue-700" },
      failed: { label: "Failed", className: "bg-rose-100 text-rose-700" }
    };
    
    const badge = badges[status];
    if (!badge) return null;
    
    return (
      <Badge className={`${badge.className} border-0 text-xs`}>
        <Brain className="w-3 h-3 mr-1" />
        {badge.label}
      </Badge>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-900">Recent Activity</h3>
      </div>
      <div className="divide-y divide-slate-100">
        {activities.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500">
            No recent activity
          </div>
        ) : (
          activities.map((activity, index) => (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="flex items-start gap-3 px-6 py-4 hover:bg-slate-50 transition-colors"
            >
              {/* Photo Thumbnail or Icon */}
              {activity.type === "photo" && activity.photo ? (
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                  {activity.photo.thumbnail_url || activity.photo.file_url ? (
                    <img
                      src={activity.photo.thumbnail_url || activity.photo.file_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  {getStatusIcon(activity.status)}
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {activity.title}
                  </p>
                  {activity.analysisStatus && getAnalysisBadge(activity.analysisStatus)}
                </div>
                
                <p className="text-xs text-slate-600">
                  {activity.subtitle}
                </p>
                
                {/* Additional Info */}
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  {activity.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{activity.location}</span>
                    </div>
                  )}
                  {activity.time && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span>
                        {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Indicator */}
              <div className="flex-shrink-0">
                {getStatusIcon(activity.status)}
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}